import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { Card } from '../../components/Card';
import { CmsSectionEditor } from '../../components/CmsSectionEditor';
import { useResellerCms, saveResellerCmsSection, clearResellerCmsSection, type AdminCmsSection } from '../../api/cms';
import { CMS_REGISTRY } from '../../content/registry';
import { SINGLE_FALLBACKS, LIST_FALLBACKS, RESELLER_LIST_OVERRIDES } from '../../content/fallbacks';

/**
 * Reseller marketing-content editor — mirrors the platform-admin Cms.tsx
 * editor, registry-driven (`content/registry.ts`) and filtered to the sections
 * flagged `resellerEditable`: the reseller's own homepage (Landing.tsx) and its
 * pricing-page sections. A reseller may have no row yet for a section — it
 * then shows the built-in default copy as a placeholder hint (or, for a
 * repeatable list, pre-filled starting content) and a "Using default copy"
 * badge; saving writes only that reseller's own scope (RLS-enforced on the
 * API), and "Reset to default" removes the override.
 */
export function Content() {
  const { data: sections = [], isLoading } = useResellerCms();
  const qc = useQueryClient();
  const byKey = new Map<string, AdminCmsSection>(sections.map((s) => [s.sectionKey, s]));
  const pages = CMS_REGISTRY.map((page) => ({ ...page, sections: page.sections.filter((s) => s.resellerEditable) })).filter(
    (page) => page.sections.length > 0,
  );

  async function onSaved() {
    await qc.invalidateQueries({ queryKey: ['reseller-cms'] });
    await qc.invalidateQueries({ queryKey: ['cms'] });
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Brand"
        title="Content"
        subtitle="Edit the copy on your own homepage and pricing page. Leave a field blank to keep the default copy."
      />
      {isLoading ? (
        <Card>
          <div className="p-6 space-y-3" aria-busy="true">
            <div className="skeleton h-5 w-40 rounded" />
            <div className="skeleton h-24 rounded" />
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {pages.map((page) => (
            <details key={page.key} className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/30" open>
              <summary className="cursor-pointer list-none px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3">
                <span className="font-semibold text-zinc-950 dark:text-zinc-50">{page.label}</span>
                <span className="text-xs text-zinc-400 dark:text-zinc-500">
                  {page.sections.length} section{page.sections.length === 1 ? '' : 's'}
                </span>
              </summary>
              <div className="px-4 sm:px-5 pb-5 space-y-4">
                {page.sections.map((section) => (
                  <CmsSectionEditor
                    key={section.key}
                    spec={section}
                    existing={byKey.get(section.key)}
                    fallbackSingle={SINGLE_FALLBACKS[section.key] ?? {}}
                    fallbackList={RESELLER_LIST_OVERRIDES[section.key] ?? LIST_FALLBACKS[section.key] ?? []}
                    save={saveResellerCmsSection}
                    clear={clearResellerCmsSection}
                    onSaved={onSaved}
                  />
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
